/**
 * Geometry utilities for the Trip Planner.
 *
 * WHY THIS FILE EXISTS:
 * When a user plans a road trip, we need to figure out which bathrooms
 * are close to their driving route. That requires some math about
 * distances on a round Earth (the Haversine formula) and perpendicular
 * distances from a point to a line segment.
 *
 * All functions are pure math — no side effects, easy to test!
 */

const EARTH_RADIUS_METERS = 6_371_000;

/**
 * Convert degrees to radians.
 * Radians are what JavaScript's Math.sin/cos expect.
 */
function toRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Calculate the distance in meters between two GPS coordinates.
 *
 * Uses the Haversine formula — accounts for the Earth being round.
 * Same formula as scripts/lib/dedup.js, but exported as an ES module
 * so the browser can use it.
 *
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lng1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lng2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Calculate the shortest distance from a point to a line segment.
 *
 * WHY WE NEED THIS:
 * A driving route is made up of hundreds of tiny line segments.
 * To check if a bathroom is "near the route," we measure the shortest
 * distance from the bathroom to each segment. This is the perpendicular
 * distance (like dropping a straight line from the point to the road).
 *
 * HOW IT WORKS:
 * 1. Project the point onto the line defined by the segment
 * 2. Clamp the projection to the segment (so we don't extend past the ends)
 * 3. Measure the distance from the point to that clamped projection
 *
 * @param {[number, number]} point - [lat, lng] of the bathroom
 * @param {[number, number]} segStart - [lat, lng] start of the route segment
 * @param {[number, number]} segEnd - [lat, lng] end of the route segment
 * @returns {number} Distance in meters
 */
export function pointToSegmentDistance(point, segStart, segEnd) {
  // If the segment has zero length, just return direct distance
  const segLen = haversineDistance(segStart[0], segStart[1], segEnd[0], segEnd[1]);
  if (segLen === 0) {
    return haversineDistance(point[0], point[1], segStart[0], segStart[1]);
  }

  // Project point onto the line using a parameter t (0 = start, 1 = end)
  // We use a flat-earth approximation here because segments are short (< 1km)
  const dx = segEnd[1] - segStart[1]; // lng difference
  const dy = segEnd[0] - segStart[0]; // lat difference
  const px = point[1] - segStart[1];
  const py = point[0] - segStart[0];

  // t tells us where the closest point on the line falls
  // t=0 means closest to start, t=1 means closest to end
  let t = (px * dx + py * dy) / (dx * dx + dy * dy);

  // Clamp t to [0, 1] so we stay on the segment (not the infinite line)
  t = Math.max(0, Math.min(1, t));

  // Calculate the closest point on the segment
  const closestLat = segStart[0] + t * dy;
  const closestLng = segStart[1] + t * dx;

  return haversineDistance(point[0], point[1], closestLat, closestLng);
}

/**
 * Find the minimum distance from a point to a polyline (series of segments).
 *
 * This checks every segment and returns the shortest distance found,
 * plus which segment index was closest (useful for figuring out how
 * far along the route the point is).
 *
 * @param {[number, number]} point - [lat, lng]
 * @param {[number, number][]} routeCoords - Array of [lat, lng] points forming the route
 * @returns {{ distance: number, segmentIndex: number }}
 */
export function pointToRouteDistance(point, routeCoords) {
  let minDistance = Infinity;
  let closestSegment = 0;

  for (let i = 0; i < routeCoords.length - 1; i++) {
    const dist = pointToSegmentDistance(point, routeCoords[i], routeCoords[i + 1]);
    if (dist < minDistance) {
      minDistance = dist;
      closestSegment = i;
    }
  }

  return { distance: minDistance, segmentIndex: closestSegment };
}

/**
 * Calculate how far along the route a segment index represents (0 to 1).
 *
 * WHY: We use this to space out bathroom stops evenly. If a bathroom
 * is at segment 50 out of 100 segments, it's roughly at the halfway
 * point of the trip (~0.5 progress).
 *
 * NOTE: This is approximate — it uses segment count, not actual distance.
 * Good enough for our spacing algorithm since route segments are fairly uniform.
 *
 * @param {number} segmentIndex - Which segment the point is closest to
 * @param {number} totalSegments - Total number of segments (routeCoords.length - 1)
 * @returns {number} Progress from 0 (start) to 1 (end)
 */
export function getRouteProgress(segmentIndex, totalSegments) {
  if (totalSegments <= 0) return 0;
  return segmentIndex / totalSegments;
}

/**
 * Simplify a route by sampling every Nth point.
 *
 * WHY: OSRM returns routes with hundreds or thousands of points.
 * Checking every bathroom against every segment would be slow.
 * Sampling every Nth point keeps it fast while still being accurate
 * (we're checking an 800m corridor, so we don't need centimeter precision).
 *
 * @param {[number, number][]} coords - Full route coordinates
 * @param {number} sampleRate - Keep every Nth point (default: 5)
 * @returns {[number, number][]} Simplified route
 */
export function simplifyRoute(coords, sampleRate = 5) {
  if (coords.length <= 2) return coords;

  const simplified = [];
  for (let i = 0; i < coords.length; i += sampleRate) {
    simplified.push(coords[i]);
  }

  // Always include the last point (the destination)
  const lastPoint = coords[coords.length - 1];
  const lastSimplified = simplified[simplified.length - 1];
  if (lastPoint[0] !== lastSimplified[0] || lastPoint[1] !== lastSimplified[1]) {
    simplified.push(lastPoint);
  }

  return simplified;
}

/**
 * Find the bathroom closest to a point.
 *
 * WHY: the search box filters bathrooms by name/address, but the map only
 * draws the ones inside the current view. When every match is somewhere
 * else (e.g. searching "Chicago" while looking at New York), the map uses
 * this to jump to the nearest match instead of showing an empty screen.
 *
 * @param {{location: {lat: number, lng: number}}[]} bathrooms
 * @param {number} lat
 * @param {number} lng
 * @returns {object|null} The nearest bathroom, or null for an empty list
 */
export function findNearestBathroom(bathrooms, lat, lng) {
  let nearest = null;
  let nearestDist = Infinity;
  for (const b of bathrooms) {
    const dist = haversineDistance(lat, lng, b.location.lat, b.location.lng);
    if (dist < nearestDist) {
      nearest = b;
      nearestDist = dist;
    }
  }
  return nearest;
}
